import { Formik } from 'formik';
import { useMutation } from 'react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { backend } from '../../axios/backend';
import CheckboxFormik from '../../components/formik/CheckboxFormik';
import InputFormik from '../../components/formik/InputFormik';
import Button from '../../components/ui/Button';
import { useAppContext } from '../../context/AppContext';
import { parseErrorMessage } from '../../utils/error-message';

const RegistrationSchema = Yup.object().shape({
  username: Yup.string().email('Bitte gib eine gültige Email Adresse an').required('Bitte gib eine Email Adresse an.'),
  password: Yup.string()
    .min(8, 'Bitte wähle ein Passwort mit mindestens 8 Zeichen.')
    .required('Bitte gib ein Passwort an.'),
  passwordConfirmation: Yup.string().oneOf([Yup.ref('password'), null], 'Die Passwörter stimmen nicht überein.'),
  firstname: Yup.string().required('Bitte gib einen Vornamen an.'),
  lastname: Yup.string().required('Bitte gib einen Nachnamen an.'),
  consent: Yup.bool().isTrue('Du musst noch unseren Geschäftsbedingungen zustimmen').required(),
});

const RegisterPage = () => {
  const navigate = useNavigate();
  const { sharedId } = useParams();
  const { preUser, alertError, alertSuccess } = useAppContext();

  const registerMutation = useMutation(
    (options: { username: string; password: string; firstname: string; lastname: string }) =>
      backend.post('auth/register', { ...options, sharedId, preUserId: preUser?.id }),
    {
      onSuccess: () => {
        alertSuccess('Erfolgreich registriert!');
        localStorage.removeItem('pre-user');
        navigate('/login');
      },
      onError: (e) => alertError(parseErrorMessage(e)),
    },
  );

  return (
    <div style={{ margin: '2rem' }} className="flex flex-col items-center justify-center">
      <div className="w-full rounded-lg shadow border md:mt-0 sm:max-w-md xl:p-0 bg-gray-800 border-gray-700">
        <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
          <h1 className="text-white text-xl">
            <b>Registrieren</b>
          </h1>
          <p className="text-white">Hier kannst du dich kostenlos registrieren.</p>
          <Formik
            initialValues={{
              username: '',
              password: '',
              passwordConfirmation: '',
              firstname: preUser?.name || '',
              lastname: '',
              consent: false,
            }}
            onSubmit={(values) => {
              registerMutation.mutate(values);
            }}
            validationSchema={RegistrationSchema}
            validateOnChange
          >
            {({ handleSubmit, isValid }) => (
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 md:space-y-6">
                  <InputFormik
                    name="username"
                    label="Email"
                    placeholder="Deine E-Mail Adresse"
                    type="email"
                    autoComplete="username"
                  />
                  <InputFormik
                    name="password"
                    label="Passwort"
                    placeholder="Dein Passwort"
                    type="password"
                    autoComplete="new-password"
                  />
                  <InputFormik
                    name="passwordConfirmation"
                    label="Nochmal dein Passwort"
                    placeholder="Dein Passwort"
                    type="password"
                    autoComplete="new-password"
                  />
                  <InputFormik name="firstname" label="Vorname" placeholder="Dein Vorname" />
                  <InputFormik name="lastname" label="Nachname" placeholder="Dein Nachname" />
                  <CheckboxFormik name="consent">
                    <span className="font-light">
                      Ich akzeptiere die{' '}
                      <Link to="/agb" className="font-medium  hover:underline text-primary-500">
                        AGBs
                      </Link>
                      .
                    </span>
                  </CheckboxFormik>
                  <Button type="submit" isLoading={registerMutation.isLoading}>
                    Registrieren
                  </Button>
                  <p className="text-sm font-light text-gray-500 dark:text-gray-400">
                    Du hast schon einen Account?{' '}
                    <Link to="/login" className="font-medium hover:underline text-primary-500">
                      Hier einloggen
                    </Link>
                  </p>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
